import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Target } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { portalPut, hu, apiError } from '../../lib/portalApi';

const ACTIVITY = [
  { v: 1.2, t: 'Ülőmunka, nincs edzés' },
  { v: 1.375, t: 'Könnyű, 1–3 edzés/hét' },
  { v: 1.55, t: 'Közepes, 3–4 edzés/hét' },
  { v: 1.725, t: 'Aktív, 4–5 edzés/hét' },
  { v: 1.9, t: 'Nagyon aktív, 6+ edzés/hét' },
];

const GOALS = [
  { v: -0.25, t: 'Verseny előtti szakasz (−25%)' },
  { v: -0.2, t: 'Szálkásítás (−20%)' },
  { v: -0.12, t: 'Mérsékelt zsírégetés (−12%)' },
  { v: 0, t: 'Szinten tartás' },
  { v: 0.08, t: 'Izomépítés (+8%)' },
  { v: 0.15, t: 'Tömegnövelés (+15%)' },
];

const DAY_TYPES = [
  { key: 'low', t: 'Alacsony CH', mult: 0.5 },
  { key: 'normal', t: 'Normál', mult: 1 },
  { key: 'high', t: 'Magas CH', mult: 1.4 },
  { key: 'refeed', t: 'Refeed', mult: 1.8 },
];

export const MacroDayTypeTab = () => {
  const [f, setF] = useState({
    gender: 'male', age: 28, height: 180, weight: 82, bodyfat: '',
    activity: 1.725, goal: -0.2, proteinPerKg: 2.2, fatPerKg: 0.8,
  });
  const [mult, setMult] = useState(Object.fromEntries(DAY_TYPES.map(d => [d.key, d.mult])));
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const calc = useMemo(() => {
    const w = Number(f.weight) || 0;
    const h = Number(f.height) || 0;
    const a = Number(f.age) || 0;
    const bf = Number(f.bodyfat) || 0;
    if (!w || !h || !a) return null;
    // Katch–McArdle, ha van testzsír % — egyébként Mifflin–St Jeor
    const bmr = bf > 0
      ? 370 + 21.6 * (w * (1 - bf / 100))
      : (10 * w + 6.25 * h - 5 * a + (f.gender === 'male' ? 5 : -161));
    const tdee = bmr * Number(f.activity);
    const targetKcal = tdee * (1 + Number(f.goal));
    const protein = w * Number(f.proteinPerKg);
    const fat = w * Number(f.fatPerKg);
    // A "Normál" nap szénhidrátja a maradék kalóriából jön, a többi nap ennek szorzata.
    const baseCarbs = Math.max(0, (targetKcal - protein * 4 - fat * 9) / 4);
    const rows = DAY_TYPES.map(d => {
      const carbs = baseCarbs * Math.min(3, Math.max(0, Number(mult[d.key]) || 0));
      return {
        ...d,
        carbs: Math.round(carbs),
        kcal: Math.round(protein * 4 + fat * 9 + carbs * 4),
      };
    });
    return {
      bmr: Math.round(bmr), tdee: Math.round(tdee), targetKcal: Math.round(targetKcal),
      protein: Math.round(protein), fat: Math.round(fat), baseCarbs: Math.round(baseCarbs),
      rows, usedKatch: bf > 0,
    };
  }, [f, mult]);

  const applyAsTarget = async (row) => {
    if (!calc) return;
    try {
      await portalPut('/food-log/targets', {
        kcal: row.kcal, protein: calc.protein, carbs: row.carbs, fat: calc.fat,
        label: `${row.t} nap`, manual: true,
      });
      toast.success(`Napi cél beállítva: ${row.t} — ${hu(row.kcal)} kcal`);
    } catch (err) { toast.error(apiError(err, 'Nem sikerült beállítani.')); }
  };

  return (
    <div data-testid="macro-daytype-tab" className="grid lg:grid-cols-2 gap-6 [&_div]:min-w-0">
      {/* Alapadatok */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="text-[11px] font-black uppercase tracking-[0.14em] text-gray-500 mb-4">Alapadatok</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-gray-500">Nem</Label>
            <select data-testid="macro-gender" value={f.gender} onChange={e => set('gender', e.target.value)}
              className="mt-1 w-full h-10 rounded-md border border-gray-300 px-3 text-sm bg-white">
              <option value="male">Férfi</option>
              <option value="female">Nő</option>
            </select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-gray-500">Életkor</Label>
            <Input data-testid="macro-age" type="number" value={f.age} onChange={e => set('age', e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-gray-500">Magasság (cm)</Label>
            <Input type="number" value={f.height} onChange={e => set('height', e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-gray-500">Testsúly (kg)</Label>
            <Input data-testid="macro-weight" type="number" value={f.weight} onChange={e => set('weight', e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-gray-500">Testzsír %</Label>
            <Input data-testid="macro-bodyfat" type="number" value={f.bodyfat} placeholder="nem kötelező"
              onChange={e => set('bodyfat', e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-gray-500">Aktivitás</Label>
            <select value={f.activity} onChange={e => set('activity', e.target.value)}
              className="mt-1 w-full h-10 rounded-md border border-gray-300 px-2 text-sm bg-white">
              {ACTIVITY.map(a => <option key={a.v} value={a.v}>{a.t}</option>)}
            </select>
          </div>
          <div className="col-span-2 sm:col-span-1">
            <Label className="text-[11px] uppercase tracking-wider text-gray-500">Cél</Label>
            <select data-testid="macro-goal" value={f.goal} onChange={e => set('goal', e.target.value)}
              className="mt-1 w-full h-10 rounded-md border border-gray-300 px-2 text-sm bg-white">
              {GOALS.map(g => <option key={g.v} value={g.v}>{g.t}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-gray-500">Fehérje (g/ttkg)</Label>
            <Input type="number" step="0.1" value={f.proteinPerKg} onChange={e => set('proteinPerKg', e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-gray-500">Zsír (g/ttkg)</Label>
            <Input type="number" step="0.1" value={f.fatPerKg} onChange={e => set('fatPerKg', e.target.value)} className="mt-1" />
          </div>
        </div>
        {calc && (
          <p className="text-xs text-gray-500 mt-4 leading-relaxed">
            Alapanyagcsere: <strong>{hu(calc.bmr)} kcal</strong>{' '}
            ({calc.usedKatch ? 'Katch–McArdle, testzsír alapján' : 'Mifflin–St Jeor'}).
            Teljes napi energiaigény: <strong>{hu(calc.tdee)} kcal</strong>.
            A céllal korrigált napi keret: <strong>{hu(calc.targetKcal)} kcal</strong>.
          </p>
        )}
      </div>

      {/* Naptípusok */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="text-[11px] font-black uppercase tracking-[0.14em] text-gray-500 mb-4">Napi célok naptípusonként</h3>
        {!calc ? (
          <p className="text-sm text-gray-500">Töltsd ki az alapadatokat.</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="daytype-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
                    <th className="text-left py-2">Naptípus</th>
                    <th className="text-left py-2">Szorzó (CH)</th>
                    <th className="text-right py-2">kcal</th>
                    <th className="text-right py-2">Fehérje</th>
                    <th className="text-right py-2">Szénh.</th>
                    <th className="text-right py-2">Zsír</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {calc.rows.map(r => (
                    <tr key={r.key} className="border-b border-gray-100" data-testid={`daytype-row-${r.key}`}>
                      <td className="py-2.5 font-semibold text-gray-900">{r.t}</td>
                      <td className="py-2.5">
                        <Input type="number" step="0.1" min="0" max="3" value={mult[r.key]}
                          data-testid={`daytype-mult-${r.key}`}
                          onChange={e => setMult(p => ({ ...p, [r.key]: e.target.value }))}
                          className="h-8 w-20" />
                      </td>
                      <td className="py-2.5 text-right font-black text-gray-900">{hu(r.kcal)}</td>
                      <td className="py-2.5 text-right text-gray-700">{hu(calc.protein)} g</td>
                      <td className="py-2.5 text-right text-gray-700">{hu(r.carbs)} g</td>
                      <td className="py-2.5 text-right text-gray-700">{hu(calc.fat)} g</td>
                      <td className="py-2.5 text-right">
                        <Button size="sm" variant="outline" data-testid={`daytype-apply-${r.key}`}
                          onClick={() => applyAsTarget(r)} className="h-7 text-[11px] font-bold">
                          <Target size={12} className="mr-1" /> Cél
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 mt-4 leading-relaxed">
              A fehérje és a zsír minden nap azonos, a szénhidrát változik a szorzóval. Ciklizálás nélkül
              használd mindig a „Normál" napot. A „Cél" gombbal az adott nap makrói lesznek az étkezési
              naplód napi célja.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default MacroDayTypeTab;
